import type {
    ITableLikeTreeCheckConfirmation,
    ITableLikeTreeCheckNotificationItem,
    ITableLikeTreeItem,
    ITableLikeTreeItemNotification,
} from 'components/TableLikeTree/context';
import type { ITopFilterConfig } from 'components/TableLikeTree/TableLikeTree';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderChangeObjectSelectionItem } from 'gql/graphql';
import { ContractObjectStatusEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { getParamsValuesKey, usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import type { TConstructionTypesIndexes } from 'pages/Project/Common/types';
import { useIncludedCountByConstructionType } from 'pages/Project/Common/WorkOrderStructure/hooks/useIncludedCountByConstructionType';
import type {
    ICRUDConfig,
    IUseWorkOrderStructureDataSourceProps,
    IUseWorkOrderStructureDataSourceResult,
    TWorkOrderStructureItem,
} from 'pages/Project/Common/WorkOrderStructure/types';
import {
    deleteTitle,
    doNotDeleteTitle,
    WorkOrderStructureOperation,
    WorkOrderStructureOperationType,
} from 'pages/Project/Common/WorkOrderStructure/types';
import { methodologyMap } from 'types/methodology';
import { Badge } from 'uikit';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUerWorkOrderChangeLastSeasonSelectionList } from '../../hooks/useUerWorkOrderChangeLastSeasonSelectionList';
import { getWorkOrderSeasons } from '../../WorkOrderChangeQueries';

import { useIncludeExcludeUerWorkOrderChangeObjectsData } from './useIncludeExcludeUerWorkOrderChangeObjectsData';
import { useUerSetWorkOrderChangeLastSeason } from './useUerSetWorkOrderChangeLastSeason';
import { useUerWorkOrderChangeObjectSelectionList } from './useUerWorkOrderChangeObjectSelectionList';

export const useGetUerWorkOrderChangeStructureDataSource = (
    props: IUseWorkOrderStructureDataSourceProps<WorkOrderChangeObjectSelectionItem>,
): IUseWorkOrderStructureDataSourceResult<WorkOrderChangeObjectSelectionItem> => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const {
        projectData,
        methodology,
        workOrderOrChangeId,
        useGetIncludedCountData,
        useIncludeExcludeWorkOrderObjects,
        isReadOnly,
        canEditObjectCode,
    } = props;

    const [unsortedConstructionTypes, constructionTypesStatus] = useContractConstructionTypes(
        projectData?.contractId,
        ProjectMethodologyEnum.Uer,
    );

    const constructionTypes = useMemo(
        () => [...unsortedConstructionTypes].sort((a, b) => a?.name?.localeCompare(b?.name || '')),
        [unsortedConstructionTypes],
    );

    const [constructionTypeId, setConstructionTypeId] = useState<number>();
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [selectedSeasonsIds] = useState<number[]>([]);

    const seasonsVariables = useMemo(() => ({ workOrderChangeId: workOrderOrChangeId }), [workOrderOrChangeId]);

    const [workOrderChangeSeasonsData, workOrderChangeSeasonsStatus, , workOrderChangeSeasonsDataRefresh] = useRequest({
        requestId: 'workOrderChangeSeasons',
        query: getWorkOrderSeasons,
        variables: seasonsVariables,
    });

    const workOrderChangeSeasonsMakeRefresh = useCallback(() => {
        dispatch(workOrderChangeSeasonsDataRefresh(seasonsVariables));
    }, [workOrderChangeSeasonsDataRefresh, seasonsVariables, dispatch]);

    const { uerWorkOrderChangeLastSeasonSelectionListData, uerWorkOrderChangeLastSeasonSelectionListStatus } =
        useUerWorkOrderChangeLastSeasonSelectionList(workOrderOrChangeId);

    const { uerWorkOrderChangeObjectSelectionListMakeRequest, uerWorkOrderChangeObjectSelectionListData } =
        useUerWorkOrderChangeObjectSelectionList();

    const areCheckboxesDisabled = useMemo(() => false, []);

    const items = useMemo(
        () =>
            uerWorkOrderChangeObjectSelectionListData?.uerWorkOrderChangeObjectSelectionList?.items?.filter?.((i) => i),
        [uerWorkOrderChangeObjectSelectionListData?.uerWorkOrderChangeObjectSelectionList?.items],
    );

    const { includedCount, updateIncludedCount } = useIncludedCountByConstructionType({
        workOrderId: workOrderOrChangeId,
        useGetIncludedCountData,
    });

    const tablePageKey = getParamsValuesKey(pathname)?.replace('_structure', '');

    const { savedFilterState, handleCleanEntityMemoizedValues } = usePageFilterSaver({
        customSaveKey: tablePageKey,
    });

    useEffect(() => {
        /* после исключения всех объектов из вида строительства очищаем сохраненные фильтры для него
         т.к этот вид строительства пропадает со страницы изм. и восстановить данные корректно нельзя */
        const currentWorkOrderChangeConstructionTypeId =
            savedFilterState?.constructionTypeId as TConstructionTypesIndexes;

        if (tablePageKey && includedCount && !includedCount?.[currentWorkOrderChangeConstructionTypeId]) {
            handleCleanEntityMemoizedValues({
                pathNameKey: tablePageKey,
            });
        }
    }, [handleCleanEntityMemoizedValues, includedCount, savedFilterState?.constructionTypeId, tablePageKey]);

    const { includeExcludeStatus, makeIncludeExcludeRequest } = useIncludeExcludeWorkOrderObjects({
        workOrderId: workOrderOrChangeId,
        constructionTypeId,
        checkedIds,
        setCheckedIds,
        updateIncludedCount,
        useIncludeExcludeWorkOrderObjectsData: useIncludeExcludeUerWorkOrderChangeObjectsData,
    });

    const { uerSetWorkOrderChangeLastSeasonStatus, uerSetWorkOrderChangeLastSeasonMakeRequest } =
        useUerSetWorkOrderChangeLastSeason({
            workOrderChangeId: workOrderOrChangeId,
            onSuccess: workOrderChangeSeasonsMakeRefresh,
        });

    const isLoading = useMemo(
        () => !projectData || constructionTypesStatus === RequestStatus.FETCHING,
        [constructionTypesStatus, projectData],
    );

    const disabledCheckBoxesItemsIds = useMemo(() => {
        if (includeExcludeStatus === RequestStatus.FETCHING) {
            return items?.map((i) => i.id);
        }

        return items?.filter((item) => item.hasActsKC2).map((i) => i.id);
    }, [includeExcludeStatus, items]);

    const checkBoxesNotifications = useMemo(
        () =>
            items?.reduce((acc: ITableLikeTreeCheckNotificationItem[], item) => {
                if (item.hasActsKC2) {
                    acc.push({
                        treeItemId: item.id,
                        message: `В составе объекта содержатся Акты КС-2${
                            item.hasOtherPrices ? ' и прочие затраты' : ''
                        }. Исключение невозможно`,
                    });
                }

                return acc;
            }, []),
        [items],
    );

    const checkConfirmations: ITableLikeTreeCheckConfirmation[] = useMemo(
        () =>
            items
                ?.filter((i) => i.hasPrices || i.hasOtherPrices)
                ?.map((i) => ({
                    treeItemId: i.id,
                    onUncheck: {
                        message: `В составе данного ${
                            i.parentId ? 'подобъекта' : 'объекта'
                        } содержатся расценки. Вы уверены, что хотите исключить ${
                            i.parentId ? 'подобъект' : 'объект'
                        } из Изм. к НЗ?`,
                        confirmLabel: 'Исключить',
                    },
                })) || [],
        [items],
    );

    const dragOperationDisabledItemsIds = useMemo(() => [], []);

    const topFilterConfig: ITopFilterConfig = useMemo(() => {
        const lastSeasons =
            uerWorkOrderChangeLastSeasonSelectionListData?.uerWorkOrderChangeLastSeasonSelectionList || [];
        const workOrderChangeSeasons = workOrderChangeSeasonsData?.uerWorkOrderChangeSeasons?.items || [];

        const years = workOrderChangeSeasons.map((s) => s.year as number);
        const firstYear = Math.min(...years);
        const lastYear = Math.max(...years);

        return {
            filterItems: workOrderChangeSeasons.map((s) => {
                const isFirstActive = firstYear === s?.year;
                const isLastActive = lastYear === s?.year;

                return { id: s?.id, title: s?.year, isFirstActive, isLastActive };
            }),
            secondListItems: lastSeasons.map((s) => ({
                id: s?.id,
                title: s?.year,
                isLastActive: lastYear === s?.year,
            })),
            title: isReadOnly ? 'Выбранные сезоны:' : 'Выбор сезонов',
            isReadOnly,
            isFictiveFilter: true,
            onFilterItemCheckChange: (seasonValues: any) => {
                if (seasonValues?.lastSeason?.id) {
                    uerSetWorkOrderChangeLastSeasonMakeRequest(seasonValues?.lastSeason?.id);
                }
            },
            isFirstListDisabled: true,
            disabled:
                workOrderChangeSeasonsStatus === RequestStatus.FETCHING ||
                uerWorkOrderChangeLastSeasonSelectionListStatus === RequestStatus.FETCHING ||
                uerSetWorkOrderChangeLastSeasonStatus === RequestStatus.FETCHING,
        };
    }, [
        isReadOnly,
        uerSetWorkOrderChangeLastSeasonMakeRequest,
        uerSetWorkOrderChangeLastSeasonStatus,
        uerWorkOrderChangeLastSeasonSelectionListData?.uerWorkOrderChangeLastSeasonSelectionList,
        workOrderChangeSeasonsData?.uerWorkOrderChangeSeasons?.items,
        workOrderChangeSeasonsStatus,
        uerWorkOrderChangeLastSeasonSelectionListStatus,
    ]);

    const informers: ITableLikeTreeItemNotification[] = useMemo(
        () =>
            items?.reduce((acc: ITableLikeTreeItemNotification[], item) => {
                const itemsToAdd = [
                    ...(item.status === ContractObjectStatusEnum.NewRecord
                        ? [{ icon: <Badge minified size="s" status="success" /> }]
                        : []),
                    ...(item.hasPrices
                        ? [
                              {
                                  message: `В составе ${item.parentId ? 'подобъекта' : 'объекта'} имеются расценки`,
                                  icon: <Badge size="s" status="system" label="Р" />,
                              },
                          ]
                        : []),
                ];

                if (itemsToAdd.length) {
                    acc.push({
                        items: itemsToAdd,
                        treeItemId: item.id,
                    });
                }

                return acc;
            }, []) || [],
        [items],
    );

    const refreshByConstructionType = useCallback(() => {
        if (workOrderOrChangeId && constructionTypeId) {
            uerWorkOrderChangeObjectSelectionListMakeRequest(workOrderOrChangeId, constructionTypeId, isReadOnly);
        }
    }, [workOrderOrChangeId, constructionTypeId, uerWorkOrderChangeObjectSelectionListMakeRequest, isReadOnly]);

    const crudMenuConfig: ICRUDConfig = useMemo(
        () => ({
            create: {
                operationSuccessCallback: () => {
                    if (constructionTypeId) {
                        updateIncludedCount(constructionTypeId, 1);
                    }

                    refreshByConstructionType();
                },
                checkDisabledCallback: (treeItem: ITableLikeTreeItem) => {
                    const item = items?.find((i) => i.id === treeItem.id);

                    return item?.hasPrices || item?.hasActsKC2 || item?.hasOtherPrices;
                },
            },
            update: {
                operationSuccessCallback: refreshByConstructionType,
                checkDisabledCallback: (treeItem: ITableLikeTreeItem) => {
                    const item = items?.find((i) => i.id === treeItem.id);

                    if (!item) {
                        return true;
                    }

                    if (item.status !== ContractObjectStatusEnum.NewRecord) {
                        return !canEditObjectCode;
                    }

                    return item.isInUse;
                },
            },
            delete: {
                operationSuccessCallback: refreshByConstructionType,
                checkDisabledCallback: (treeItem: ITableLikeTreeItem) => {
                    const item: any = items?.find((i) => i.id === treeItem.id);

                    return item?.status !== ContractObjectStatusEnum.NewRecord || item?.isInUse || item?.hasActsKC2;
                },
                getTooltipCallback: (treeItem: ITableLikeTreeItem) => {
                    const item: any = items?.find((i) => i.id === treeItem.id);

                    if (item?.hasActsKC2) {
                        return {
                            haveTooltip: true,
                            tooltipName: `no_delete_${item?.id}`,
                            tooltipMsg: 'В составе объекта имеются Акты КС-2. Удаление невозможно',
                        };
                    }

                    return undefined;
                },
            },
        }),
        [canEditObjectCode, constructionTypeId, items, refreshByConstructionType, updateIncludedCount],
    );

    const onItemCheckChange = useCallback(
        (item: ITableLikeTreeItem, isChecked: boolean) => {
            makeIncludeExcludeRequest?.(
                isChecked ? WorkOrderStructureOperationType.Include : WorkOrderStructureOperationType.Exclude,
                item.id,
            );
        },
        [makeIncludeExcludeRequest],
    );

    const complete = useCallback(() => {
        if (methodology) {
            navigate(
                `/projects/${methodologyMap[methodology]}/${projectData?.uniqueId}/orderChange/${workOrderOrChangeId}`,
            );
        }
    }, [methodology, navigate, projectData?.uniqueId, workOrderOrChangeId]);

    const getStructureOperationConfirmationMessage = useCallback(
        (
            operation?: WorkOrderStructureOperation,
            structureItem?: TWorkOrderStructureItem<WorkOrderChangeObjectSelectionItem>,
        ) => {
            if (
                [WorkOrderStructureOperation.DeleteObject, WorkOrderStructureOperation.DeleteSubObject].includes(
                    operation as WorkOrderStructureOperation,
                )
            ) {
                const item = items?.find((i) => i.id === structureItem?.id);

                if (item?.hasPrices || item?.hasOtherPrices) {
                    const objectName = item?.parentId ? 'подобъект' : 'объект';

                    return {
                        textConfirmation: `В составе ${objectName}а содержатся расценки. Вы уверены, что хотите удалить ${objectName}? Все дочерние элементы будут удалены.`,
                        textHeader: `Удаление ${objectName}а`,
                        cancelButtonText: doNotDeleteTitle,
                        confirmButtonText: deleteTitle,
                    };
                }
            }

            return undefined;
        },
        [items],
    );

    useEffect(() => {
        if (constructionTypes?.length) {
            setConstructionTypeId(constructionTypes[0].id);
        }
    }, [constructionTypes]);

    useEffect(() => {
        refreshByConstructionType();
    }, [refreshByConstructionType]);

    useEffect(() => {
        if (items?.length) {
            const checkedItems = items.filter((i) => i.isIncluded).map((i) => i.id);

            setCheckedIds(checkedItems);
        }
    }, [items, constructionTypeId]);

    return {
        constructionTypes,
        setConstructionTypeId,
        seasons: workOrderChangeSeasonsData?.uerWorkOrderChangeSeasons?.items,
        items,
        isLoading,
        checkedIds,
        onItemCheckChange,
        areCheckboxesDisabled,
        complete,
        isCompletePerforming: false,
        topFilterConfig,
        disabledCheckBoxesItemsIds,
        dragOperationDisabledItemsIds,
        informers,
        selectedSeasonsIds,
        crudMenuConfig,
        constructionTypeId,
        includedCount,
        isHeaderCheckboxDisabled: true,
        checkBoxesNotifications,
        checkConfirmations,
        getStructureOperationConfirmationMessage,
    };
};
